/**
 * UPT-2079 P2b (web §4) — démonstration de la traduction en tâche de fond : une tâche de 15 lots (cinq par langue : allemand, italien, arabe) qui avance d'UN lot par
 * seconde. L'état est calculé à partir de l'heure (déterministe, testable avec des minuteries simulées) : aucun minuteur, aucun appel réseau. À la fin, la version suivante est
 * créée en mémoire une seule fois (`materialize`, comme la traduction synchrone de démonstration). `cancel` gèle les lots faits, `resume` repart du lot suivant.
 */
import type { ModuleSpecTranslationJob, TranslationLocale } from './contracts';
import type { ModuleSpecVersion } from './moduleSpecContracts';
import { designError } from '../specs/designErrors';

export interface DemoTranslationHost {
  /** Lève `modulespec.translate.{state, nothing}` ou 404 si la version ne se traduit pas ; ne crée rien. */
  assertTranslatable(dossierId: string, version: number): void;
  /** Crée en mémoire la version traduite (une seule fois par tâche) et rend son numéro. */
  materialize(dossierId: string, version: number): Promise<ModuleSpecVersion>;
  author(): string;
}

const LOCALES: TranslationLocale[] = ['de', 'it', 'ar'];
const BATCHES_PER_LOCALE = 5;
const TOTAL = LOCALES.length * BATCHES_PER_LOCALE;
const TEXTS_PER_LOCALE = 189;
const TRANSLATIONS_TOTAL = TEXTS_PER_LOCALE * LOCALES.length;
const MAX_COST = 0.0079;
const SECONDS_PER_BATCH = 20;
const round4 = (value: number): number => Math.round(value * 10000) / 10000;

interface DemoJob {
  id: string; dossierId: string; sourceVersion: number; createdAtMs: number; createdBy: string; attempts: number;
  /** Début de la tentative en cours et lots déjà faits à ce moment-là. */
  runStartMs: number; baseDone: number; stopped: boolean; stoppedAtMs: number | null; resultVersion: number | null;
}

export class DemoTranslationJobs {
  private readonly jobs: DemoJob[] = [];
  private sequence = 0;

  constructor(private readonly host: DemoTranslationHost, private readonly clock: () => number = () => Date.now()) {}

  private doneOf(job: DemoJob, now: number): number {
    if (job.stopped) return job.baseDone;
    const end = job.resultVersion !== null && job.stoppedAtMs !== null ? job.stoppedAtMs : now;
    return Math.min(TOTAL, job.baseDone + Math.max(0, Math.floor((end - job.runStartMs) / 1000)));
  }

  /** Crée la version traduite dès que les 15 lots sont faits ; l'état rendu est alors `done`. */
  private async settle(job: DemoJob): Promise<void> {
    if (job.stopped || job.resultVersion !== null || this.doneOf(job, this.clock()) < TOTAL) return;
    job.stoppedAtMs = job.runStartMs + (TOTAL - job.baseDone) * 1000;
    job.resultVersion = -1; // verrou : une seule création même si deux lectures se croisent
    const created = await this.host.materialize(job.dossierId, job.sourceVersion);
    job.resultVersion = created.version;
  }

  private view(job: DemoJob): ModuleSpecTranslationJob {
    const now = this.clock();
    const done = this.doneOf(job, now);
    const finished = job.resultVersion !== null && done >= TOTAL;
    const status = job.stopped ? 'cancelled' : finished ? 'done' : 'running';
    const active = status === 'running';
    const perLocale = LOCALES.map((locale, index) => {
      const batchesDone = Math.min(BATCHES_PER_LOCALE, Math.max(0, done - index * BATCHES_PER_LOCALE));
      return { locale, batchesTotal: BATCHES_PER_LOCALE, batchesDone, translationsTotal: TEXTS_PER_LOCALE,
        translationsAccepted: Math.round(TEXTS_PER_LOCALE * batchesDone / BATCHES_PER_LOCALE) };
    });
    const translationsDone = perLocale.reduce((sum, entry) => sum + entry.translationsAccepted, 0);
    const currentLocale = LOCALES[Math.min(LOCALES.length - 1, Math.floor(done / BATCHES_PER_LOCALE))];
    const endMs = job.stopped ? job.stoppedAtMs ?? now : finished ? job.stoppedAtMs ?? now : now;
    return {
      id: job.id, dossierId: job.dossierId, sourceVersion: job.sourceVersion, status, locales: [...LOCALES],
      batchesTotal: TOTAL, batchesDone: done, translationsTotal: TRANSLATIONS_TOTAL, translationsDone, translationsAccepted: translationsDone,
      translationsRejected: 0, skippedPii: 0, skippedOther: 0, perLocale,
      current: active ? { seq: done, locale: currentLocale, indexInLocale: done % BATCHES_PER_LOCALE, ofLocale: BATCHES_PER_LOCALE } : null,
      route: 'geode', provider: 'OPENROUTER', model: 'claude-haiku-5-5', promptTokens: done * 550, completionTokens: done * 200,
      estimatedCostChf: MAX_COST, remainingEstimatedCostChf: done >= TOTAL ? null : round4(MAX_COST * (TOTAL - done) / TOTAL),
      actualCostChf: done === 0 ? null : round4(MAX_COST * done / TOTAL),
      errorCode: null, resumable: status === 'cancelled' && done < TOTAL, cancelRequested: false,
      resultVersion: finished && (job.resultVersion ?? -1) > 0 ? job.resultVersion : null, attempts: job.attempts,
      createdBy: job.createdBy, createdAt: new Date(job.createdAtMs).toISOString(), startedAt: new Date(job.runStartMs).toISOString(),
      heartbeatAt: active ? new Date(now).toISOString() : null, finishedAt: active ? null : new Date(endMs).toISOString(),
      elapsedSeconds: Math.max(0, Math.floor((endMs - job.runStartMs) / 1000)),
      estimatedRemainingSeconds: active ? (TOTAL - done) * SECONDS_PER_BATCH : null,
    };
  }

  private requireJob(dossierId: string, jobId: string): DemoJob {
    const job = this.jobs.find(entry => entry.id === jobId && entry.dossierId === dossierId);
    if (!job) throw designError('modulespec.translate.job_not_found', 'job');
    return job;
  }

  async create(dossierId: string, version: number): Promise<ModuleSpecTranslationJob> {
    this.host.assertTranslatable(dossierId, version);
    for (const entry of this.jobs.filter(job => job.dossierId === dossierId)) await this.settle(entry);
    const active = this.jobs.find(entry => entry.dossierId === dossierId && this.view(entry).status === 'running');
    if (active) throw designError('modulespec.translate.running', active.id);
    const now = this.clock();
    this.sequence += 1;
    const job: DemoJob = { id: `demo-translation-job-${this.sequence}`, dossierId, sourceVersion: version, createdAtMs: now, createdBy: this.host.author(),
      attempts: 1, runStartMs: now, baseDone: 0, stopped: false, stoppedAtMs: null, resultVersion: null };
    this.jobs.push(job);
    return this.view(job);
  }

  async list(dossierId: string, limit = 5): Promise<ModuleSpecTranslationJob[]> {
    const own = this.jobs.filter(entry => entry.dossierId === dossierId);
    for (const entry of own) await this.settle(entry);
    return own.slice().reverse().slice(0, Math.min(20, Math.max(1, limit))).map(entry => this.view(entry));
  }

  async get(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    const job = this.requireJob(dossierId, jobId);
    await this.settle(job);
    return this.view(job);
  }

  async cancel(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    const job = this.requireJob(dossierId, jobId);
    await this.settle(job);
    const status = this.view(job).status;
    if (status === 'cancelled') return this.view(job);
    if (status !== 'running') throw designError('modulespec.translate.not_cancellable', status);
    const now = this.clock();
    job.baseDone = this.doneOf(job, now);
    job.stopped = true; job.stoppedAtMs = now;
    return this.view(job);
  }

  async resume(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    const job = this.requireJob(dossierId, jobId);
    await this.settle(job);
    const view = this.view(job);
    if (!view.resumable) throw designError('modulespec.translate.not_resumable', 'status');
    if (this.jobs.some(entry => entry !== job && entry.dossierId === dossierId && this.view(entry).status === 'running')) throw designError('modulespec.translate.running', job.id);
    job.stopped = false; job.stoppedAtMs = null; job.runStartMs = this.clock(); job.attempts += 1;
    return this.view(job);
  }
}
